import { ChangeEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiIcon,
  MiIconButton,
  MiPlaceholderArea,
  MiSearchInput,
} from "@miplatform/components";
import {
  COST_CALCULATION_TYPE,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import {
  ICategoryPricingDetailsResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import { getCurrencyString } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiPrinterOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import {
  CategoryPricingAccordion,
  CategoryPricingSummaryCard,
  PricingSummaryTable,
} from "../../components";
import { customerItemsPricingTabSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerItemsPricingTabViewModel } from "./useCustomerItemsPricingTabViewModel";

type CustomerItemsPricingFormData = {
  categoryPricingModifiers: Array<ICategoryPricingDetailsResponse>;
  itemPricingByCategory: Record<string, Array<IMaterialsResponse>>;
};

export function CustomerItemsPricingTab() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const { classes, theme } = useStyles();
  const {
    accountDetails,
    tenantDetails,
    setSaveHandler,
    setConfirmModalConfig,
  } = useCustomerPricingDetailsContext();

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
  };

  const {
    groupedCustomerMaterials,
    categoryPricingDetails,
    handleGetCategoryPricing,
    handleGetCustomerMaterials,
    handleUpdateCategoryModifiers,
    handleUpdateCustomerMaterials,
    handlePrintItems,
    getCategoryPricingDetailsWithMaterials,
    getItemPricingTotals,
    isLoading,
  } = useCustomerItemsPricingTabViewModel({ tenantDetails });

  const form = useForm<CustomerItemsPricingFormData>({
    mode: "onChange",
    defaultValues: {
      categoryPricingModifiers: [],
      itemPricingByCategory: {},
    },
    resolver: zodResolver(customerItemsPricingTabSchema),
  });
  const { control, reset, setValue, getValues } = form;

  useTabFormRegistration(form, isLoading);

  const hasCategories = categoryPricingDetails.length > 0;

  useEffect(() => {
    if (accountDetails) {
      handleGetCustomerMaterials(accountDetails.id);
      handleGetCategoryPricing(accountDetails.id);
    }
  }, [accountDetails]);

  useEffect(() => {
    setSaveHandler(async () => {
      const accountId = accountDetails?.id ?? "";
      const { dirtyFields } = form.formState;

      const categoryRequest = dirtyFields.categoryPricingModifiers
        ? handleUpdateCategoryModifiers(
            accountId,
            getValues("categoryPricingModifiers").map((category) => ({
              categoryId: category.categoryId,
              multiplier: category.multiplier ?? null,
              markup: category.markup ?? null,
              modifiedOn: category.modifiedOn ?? "",
            }))
          )
        : null;

      const materialsRequest = dirtyFields.itemPricingByCategory
        ? handleUpdateCustomerMaterials(
            accountId,
            getValues("itemPricingByCategory")
          )
        : null;

      const [categoryResult, materialsResult] = await Promise.all([
        categoryRequest,
        materialsRequest,
      ]);

      const response = categoryResult ?? materialsResult ?? null;

      if (response) {
        reset(getValues());
      }

      return response;
    });
  }, [accountDetails]);

  useEffect(() => {
    setConfirmModalConfig({
      description: `You are about to update items pricing for ${
        accountDetails?.name || "this account"
      }. Any in-progress bids' price values may be affected. Are you sure you would like to continue?`,
    });
  }, [accountDetails]);

  useEffect(() => {
    reset({
      categoryPricingModifiers: categoryPricingDetails,
      itemPricingByCategory: groupedCustomerMaterials,
    });
  }, [categoryPricingDetails, groupedCustomerMaterials]);

  const itemPricingTotals = getItemPricingTotals(
    getValues("itemPricingByCategory")
  );

  const categoryPricingWithMaterials = getCategoryPricingDetailsWithMaterials({
    categoryPricingDetails: getValues("categoryPricingModifiers"),
    groupedCustomerMaterials: getValues("itemPricingByCategory"),
  });

  const renderPrintOutput = () => (
    <Box display="flex" flexDirection="column">
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Typography
          fontWeight={600}
          mb={1}
        >{`${accountDetails?.name}'s Item Pricing`}</Typography>
        <PricingSummaryTable
          columns={["", "Total Cost", "Total Sale Price", "Total Margin"]}
          rows={[
            [
              "",
              getCurrencyString(Number(itemPricingTotals.totalCost)),
              getCurrencyString(Number(itemPricingTotals.totalSalesPrice)),
              getCurrencyString(Number(itemPricingTotals.totalMargin)),
            ],
          ]}
        />
      </Box>
      {categoryPricingWithMaterials.map((category) => (
        <CategoryPricingSummaryCard
          key={category.categoryId}
          category={category}
        />
      ))}
    </Box>
  );

  return (
    <ConfigAccordionHeader
      title="Items Pricing"
      disableCollapse
      customAction={
        <Box>
          <MiSearchInput
            className={classes.searchContainer}
            value={search}
            handleChange={handleSearchChange}
          />
          <MiIconButton
            data-testid="mi-orders-print-icon-button"
            onClick={() => handlePrintItems(renderPrintOutput())}
          >
            <MiIcon
              path={mdiPrinterOutline}
              color={theme.palette.common.white}
            />
          </MiIconButton>
        </Box>
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          color={theme.palette.text.secondary}
        >
          Set a Multiplier or Markup, then enter a Net Sales Price or adjust the
          Margin per item.
        </Typography>
        {!hasCategories && (
          <MiPlaceholderArea
            text="No Items or Categories Items Created"
            helperText="Tip: To set pricing for items, setup the categories first."
            iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
            containerStyle={{
              backgroundColor: theme.palette.primary.light,
              height: "100%",
              minHeight: 450,
            }}
          />
        )}

        {hasCategories &&
          categoryPricingDetails.map((category, categoryIndex) => {
            const valueType =
              category.costCalculation === COST_CALCULATION_TYPE.LIST
                ? "multiplier"
                : "markup";
            const fieldLabel =
              valueType === "multiplier" ? "Multiplier" : "Markup (%)";
            const fieldName =
              `categoryPricingModifiers.${categoryIndex}.${valueType}` as const;
            const allMaterials =
              groupedCustomerMaterials[category.categoryId] ?? [];
            const materials = debouncedSearch
              ? allMaterials.filter((m) =>
                  `${m.manufacturerPartNumber} | ${m.category.itemDescription}`
                    .toLowerCase()
                    .includes(debouncedSearch.toLowerCase().trim())
                )
              : allMaterials;

            return (
              <CategoryPricingAccordion<CustomerItemsPricingFormData>
                key={category.categoryId}
                categoryName={category.categoryName}
                categoryId={category.categoryId}
                fieldName={fieldName}
                fieldLabel={fieldLabel}
                formControl={control}
                setValue={setValue}
                getValues={getValues}
                valueType={valueType}
                disabled={isLoading}
                materials={materials}
                search={debouncedSearch}
              />
            );
          })}
      </Box>
    </ConfigAccordionHeader>
  );
}

const useStyles = makeStyles()((theme) => ({
  searchContainer: {
    width: 268,
    marginTop: theme.spacing(0.75),
    marginRight: theme.spacing(2),
    backgroundColor: "transparent !important",
    minHeight: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
}));
